import {
  CREATE_PACKAGE_CATALOG_RELATION,
  DELETE_PACKAGE_CATALOG_RELATION,
  FETCH_PACKAGE_CATALOG_RELATIONS_BY_PACKAGE,
} from "@apiUtils";
import { mutationFetch } from "@atlas/sdk";
import type { IHubPackageCatalogRelation } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  HUB_CATALOG_BY_ID_AND_VERSION_KEY,
  HUB_CATALOG_BY_ID_KEY,
  HUB_CATALOGS_KEY,
} from "./HubCatalogs";

export const HUB_PACKAGE_CATALOG_RELATIONS_KEY =
  "hubPackageCatalogRelationsKey";
export const HUB_PACKAGE_CATALOG_RELATIONS_BY_PACKAGE_KEY =
  "hubPackageCatalogRelationsByPackageKey";

export const getHubPackageCatalogRelationsKey = () =>
  [HUB_PACKAGE_CATALOG_RELATIONS_KEY] as const;

export const getHubPackageCatalogRelationsByPackageKey = (packageId: number) =>
  [HUB_PACKAGE_CATALOG_RELATIONS_BY_PACKAGE_KEY, packageId] as const;

export const useHubPackageCatalogRelationsByPackage = (
  packageId: number,
  options?: Partial<
    UseQueryOptions<
      IHubPackageCatalogRelation[],
      AxiosError,
      IHubPackageCatalogRelation[],
      ReturnType<typeof getHubPackageCatalogRelationsByPackageKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubPackageCatalogRelationsByPackageKey(packageId),
    queryFn: async () => {
      return mutationFetch<IHubPackageCatalogRelation[]>({
        url: FETCH_PACKAGE_CATALOG_RELATIONS_BY_PACKAGE.replace(
          ":packageId",
          String(packageId),
        ),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && packageId > 0,
  });
};

const createHubPackageCatalogRelation = (
  body: IHubPackageCatalogRelation,
): Promise<IHubPackageCatalogRelation> => {
  return mutationFetch<IHubPackageCatalogRelation>({
    url: CREATE_PACKAGE_CATALOG_RELATION,
    method: "POST",
    body,
  });
};

const deleteHubPackageCatalogRelation = (
  packageId: number,
  catalogId: number,
): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_PACKAGE_CATALOG_RELATION.replace(
      ":packageId",
      String(packageId),
    ).replace(":catalogId", String(catalogId)),
    method: "DELETE",
  });
};

export const useCreateHubPackageCatalogRelation = (
  options?: UseMutationOptions<
    IHubPackageCatalogRelation,
    AxiosError,
    IHubPackageCatalogRelation
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubPackageCatalogRelation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_CATALOG_RELATIONS_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
      if (data.packageId != null && data.packageId > 0) {
        queryClient.invalidateQueries({
          queryKey: getHubPackageCatalogRelationsByPackageKey(data.packageId),
        });
      }
    },
    ...options,
  });
};

interface IDeletePackageCatalogRelationParams {
  packageId: number;
  catalogId: number;
}

export const useDeleteHubPackageCatalogRelation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    IDeletePackageCatalogRelationParams
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, catalogId }) =>
      deleteHubPackageCatalogRelation(packageId, catalogId),
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: [HUB_PACKAGE_CATALOG_RELATIONS_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: getHubPackageCatalogRelationsByPackageKey(packageId),
      });
    },
    ...options,
  });
};
